import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "~/components/Button";
import { Checkbox } from "~/components/Checkbox";
import { SelectInput } from "~/components/SelectInput";
import { MaskedInput, TextInput } from "~/components/TextInput";
import { type CreatePlanDTO, createPlanSchema } from "~/modules/sales/schemas";
import { createPlan, translatePaymentInterval } from "~/modules/sales/services";
import {
	pagarmeIntervaOptions,
	paymentMethodOptions,
} from "~/modules/sales/variables";
import { insertToast } from "~/modules/toasts/services";

import { ItemsInput } from "./ItemsInput";
import { Preview } from "./Preview";

type Props = {
	onSuccess?: () => void;
	isCourtesyOnly?: boolean;
};

export const CreatePlanForm: React.FC<Props> = ({
	onSuccess,
	isCourtesyOnly,
}) => {
	const {
		register,
		control,
		watch,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<CreatePlanDTO>({
		resolver: zodResolver(createPlanSchema),
		defaultValues: {
			installments: 1,
			visible: true,
			paymentMethods: isCourtesyOnly ? ["credit_card"] : [],
			items: isCourtesyOnly
				? [
						{
							name: "Plano Cortesia",
							pricing_scheme: { price: 0 },
							quantity: 1,
						},
					]
				: undefined,
		},
	});

	const onFormSubmit = useCallback(
		async (formData: CreatePlanDTO) => {
			const payload: CreatePlanDTO & { isCourtesy?: boolean } = { ...formData };

			// Courtesy plans are only ever created from the dedicated "Cortesias"
			// tab (isCourtesyOnly); the regular plan flow never sets isCourtesy.
			if (isCourtesyOnly) {
				payload.isCourtesy = true;
				payload.paymentMethods = ["credit_card"];
				payload.items = [
					{ name: "Plano Cortesia", pricing_scheme: { price: 0 }, quantity: 1 },
				];
				payload.installments = 1;
			}

			await createPlan(payload)
				.then((data) => {
					if (data) {
						insertToast({
							duration: 3,
							title: "Sucesso!",
							message: "Plano cadastrado.",
						});
						onSuccess && onSuccess();
					}
				})
				.catch((e: Error) => {
					insertToast({
						duration: 3,
						type: "error",
						title: "Erro!",
						message: e.message,
					});
				});
		},
		[onSuccess, isCourtesyOnly],
	);

	return (
		<form
			className="grid grid-cols-1 gap-4"
			onSubmit={handleSubmit(onFormSubmit)}
		>
			<div className="flex flex-col md:flex-row gap-4 bg-slate-100 p-3 rounded-lg">
				<TextInput
					label="Nome do plano"
					placeholder="Ex.: Plano Gold"
					{...register("name")}
					error={errors.name?.message}
				/>
				<Controller
					control={control}
					name="licensesAmount"
					render={({ field }) => (
						<MaskedInput
							mask="integer"
							label="Quantidade de licenças"
							placeholder="Ex.: 5"
							value={field.value}
							onChange={field.onChange}
							error={errors.licensesAmount?.message}
						/>
					)}
				/>
			</div>

			{!isCourtesyOnly && (
				<>
					<p>Formas de pagamento</p>
					<div className="flex flex-col gap-3">
						<div className="flex gap-4 justify-around bg-slate-100 p-3 rounded-lg">
							{paymentMethodOptions.map((pm) => (
								<Checkbox
									key={pm.label + "_create_plan"}
									{...register("paymentMethods", {
										setValueAs: (v) => (v ? [...v] : []),
									})}
									label={pm.label}
									value={pm.value}
									error={!!errors.paymentMethods?.message}
								/>
							))}
						</div>
						{errors.paymentMethods?.message ? (
							<p className="text-xs text-gpa-red">
								{errors.paymentMethods?.message}
							</p>
						) : null}
					</div>
				</>
			)}

			<p>Periodicidade</p>
			<div className="flex flex-col gap-3 bg-slate-100 p-3 rounded-lg">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
					<Controller
						control={control}
						name="interval"
						render={({ field }) => (
							<SelectInput
								label="Recorrência"
								options={pagarmeIntervaOptions}
								value={field.value}
								onChange={field.onChange}
								error={errors.interval?.message}
							/>
						)}
					/>
					<Controller
						control={control}
						name="intervalCount"
						render={({ field }) => (
							<MaskedInput
								label="Intervalo"
								mask="integer"
								inputMode="decimal"
								value={field.value}
								onChange={field.onChange}
								error={errors.intervalCount?.message}
							/>
						)}
					/>
				</div>

				<p>
					{translatePaymentInterval(
						watch("interval"),
						watch("intervalCount") ?? 0,
					)}
				</p>
			</div>

			{!isCourtesyOnly && (
				<>
					<p>Parcelamento</p>
					<div className="flex flex-col gap-3 bg-slate-100 p-3 rounded-lg">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
							<Controller
								control={control}
								name="installments"
								render={({ field }) => (
									<SelectInput
										label="Máximo de parcelas"
										options={Array.from({ length: 12 }, (_, i) => ({
											label: String(i + 1),
											value: i + 1,
										}))}
										value={field.value}
										onChange={field.onChange}
										error={errors.installments?.message}
									/>
								)}
							/>
						</div>
					</div>
				</>
			)}

			{!isCourtesyOnly && (
				<>
					<p>Itens do plano</p>
					<div>
						<Controller
							control={control}
							name="items"
							render={({ field }) => (
								<ItemsInput
									value={field.value}
									onChange={field.onChange}
									error={errors.items}
								/>
							)}
						/>
					</div>
				</>
			)}

			{!isCourtesyOnly && (
				<div className="flex items-center gap-3 bg-slate-100 p-3 rounded-lg">
					<Checkbox
						label="Exibir na lista de ofertas para clientes"
						{...register("visible")}
						defaultChecked
					/>
				</div>
			)}

			<p>Pré-visualização</p>
			<Preview data={watch()} />

			<hr />

			<Button loading={isSubmitting}>Cadastrar Plano</Button>
		</form>
	);
};
